import { FONT_STACK } from '@/lib/brand';

// Shown to the customer once their album is submitted (orders.status =
// album-submitted / in-review). The editor and flow pages render this instead
// of an editable surface — no edit controls, no resume link. Admins bypass it
// (regen flow) at the call site.
//
// Aesthetic: a warm, unhurried "keepsake received" moment. Beige canvas with a
// soft gold glow, a wax-seal-style mark that settles into place, a serif
// headline, and a single reassurance line. Calm and premium, not a toast.

const C = {
  bg: '#FAF7F2',
  bgGlow: '#F0E4C9',
  card: '#FFFFFF',
  gold: '#C9A24A',
  goldDeep: '#B89240',
  goldSoft: '#F0E4C9',
  ink: '#2E2418',
  sub: '#6B5D4F',
  faint: '#9C8F7E',
  sage: '#7A8B6F',
  border: '#E8E0D2',
};

export function AlbumSubmittedScreen({ destination }: { destination?: string | null }) {
  const dest = (destination ?? '').trim();

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-6"
      style={{
        background: `radial-gradient(120% 80% at 50% -10%, ${C.bgGlow} 0%, ${C.bg} 55%)`,
        fontFamily: FONT_STACK.body,
        minHeight: '100dvh',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <style>{KEYFRAMES}</style>

      {/* Soft grain/texture vignette for depth */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0"
        style={{
          background:
            'radial-gradient(60% 50% at 50% 40%, transparent 55%, rgba(46,36,24,0.04) 100%)',
        }}
      />

      <div
        className="relative w-full max-w-md text-center"
        style={{ animation: 'soumisRise 760ms cubic-bezier(0.22,1,0.36,1) both' }}
      >
        {/* ─── Wax-seal mark ─── */}
        <div className="relative mx-auto mb-9" style={{ width: 104, height: 104 }}>
          {/* breathing halo */}
          <span
            aria-hidden
            className="absolute inset-0 rounded-full"
            style={{
              background: `radial-gradient(circle, ${C.goldSoft} 0%, transparent 70%)`,
              transform: 'scale(1.7)',
              animation: 'soumisHalo 3.6s ease-in-out infinite',
            }}
          />
          <div
            className="relative flex items-center justify-center rounded-full"
            style={{
              width: 104,
              height: 104,
              background: `linear-gradient(145deg, ${C.gold} 0%, ${C.goldDeep} 100%)`,
              boxShadow: `0 18px 38px -12px ${C.gold}80, inset 0 2px 4px rgba(255,255,255,0.45), inset 0 -3px 6px rgba(0,0,0,0.12)`,
              animation: 'soumisSeal 700ms cubic-bezier(0.34,1.56,0.64,1) 120ms both',
            }}
          >
            {/* engraved ring */}
            <span
              aria-hidden
              className="absolute rounded-full"
              style={{ inset: 10, border: '1.5px solid rgba(255,255,255,0.45)' }}
            />
            <svg width="46" height="46" viewBox="0 0 46 46" fill="none">
              <path
                d="M13 24.5 L20 31.5 L34 16"
                stroke="#FFFFFF"
                strokeWidth="3.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  strokeDasharray: 48,
                  strokeDashoffset: 48,
                  animation: 'soumisCheck 520ms ease-out 560ms forwards',
                }}
              />
            </svg>
          </div>
        </div>

        {/* ─── Copy ─── */}
        <p
          className="uppercase"
          style={{
            color: C.gold,
            fontSize: 12,
            letterSpacing: '0.28em',
            fontWeight: 600,
            marginBottom: 14,
            animation: 'soumisFade 600ms ease 360ms both',
          }}
        >
          Bien reçu
        </p>

        <h1
          style={{
            fontFamily: FONT_STACK.heading,
            color: C.ink,
            fontSize: 'clamp(30px, 8vw, 40px)',
            lineHeight: 1.12,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            marginBottom: 16,
            animation: 'soumisFade 600ms ease 460ms both',
          }}
        >
          Votre album est soumis
        </h1>

        <p
          style={{
            color: C.sub,
            fontSize: 16,
            lineHeight: 1.65,
            maxWidth: 360,
            margin: '0 auto',
            animation: 'soumisFade 600ms ease 580ms both',
          }}
        >
          {dest ? (
            <>
              Votre album <span style={{ color: C.ink, fontWeight: 600 }}>{dest}</span> est
              entre nos mains — nous le préparons avec soin&nbsp;🙏
            </>
          ) : (
            <>Nous le préparons avec soin&nbsp;🙏</>
          )}
        </p>

        {/* hairline divider */}
        <div
          className="mx-auto"
          style={{
            width: 56,
            height: 1,
            background: `linear-gradient(90deg, transparent, ${C.border}, transparent)`,
            margin: '28px auto 18px',
            animation: 'soumisFade 600ms ease 700ms both',
          }}
        />

        <p
          style={{
            color: C.faint,
            fontSize: 13.5,
            lineHeight: 1.6,
            maxWidth: 320,
            margin: '0 auto',
            animation: 'soumisFade 600ms ease 780ms both',
          }}
        >
          L'équipe Storiz vous contactera sur WhatsApp pour la confirmation.
        </p>
      </div>
    </div>
  );
}

const KEYFRAMES = `
@keyframes soumisRise {
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes soumisSeal {
  from { opacity: 0; transform: scale(0.5) rotate(-12deg); }
  to   { opacity: 1; transform: scale(1) rotate(0deg); }
}
@keyframes soumisCheck {
  to { stroke-dashoffset: 0; }
}
@keyframes soumisHalo {
  0%, 100% { opacity: 0.55; transform: scale(1.55); }
  50%      { opacity: 0.9;  transform: scale(1.85); }
}
@keyframes soumisFade {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  [style*="soumis"] { animation: none !important; }
}
`;
